/**
 * Nexcaliber Member App Privacy Policy content.
 *
 * Source of truth: "Nexcaliber Privacy Policy Update — Implementation Reference", Section 3.
 * This text is compared by Apple App Review against the app and the App Privacy form.
 * Edit wording here only when the approved policy text changes, and keep it word for word.
 *
 * Email addresses inside any text are rendered as mailto: links automatically.
 */

/** A run of text. A string is plain text; `{ bold }` is rendered as <strong>. */
export type Inline = string | { bold: string };

export type PolicyBlock =
	| { kind: "paragraph"; content: Inline[] }
	| { kind: "list"; items: Inline[][] }
	| { kind: "address"; lines: Inline[][] };

export type PolicySectionId =
	| "information-we-collect"
	| "how-we-collect-information"
	| "how-we-use-information"
	| "hipaa-compliance"
	| "sharing-of-information"
	| "data-security"
	| "data-retention"
	| "account-deletion"
	| "your-rights"
	| "children-and-dependents"
	| "policy-updates"
	| "contact";

export interface PolicySection {
	id: PolicySectionId;
	number: number;
	title: string;
	blocks: PolicyBlock[];
	/** Gives the section a subtle highlighted panel. Visual only. */
	emphasis?: boolean;
}

export interface PolicyDocument {
	title: string;
	effectiveDate: string;
	lastUpdated: string;
	intro: PolicyBlock[];
	sections: PolicySection[];
}

/** Publish date. Update both dates here when a revised policy is published. */
const PUBLISH_DATE = "November 4, 2026";

export const CONTACT_EMAIL = "info@nexcaliber.com";

export const PRIVACY_POLICY: PolicyDocument = {
	title: "HIPAA-Compliant Privacy Policy",
	effectiveDate: PUBLISH_DATE,
	lastUpdated: PUBLISH_DATE,
	intro: [
		{
			kind: "paragraph",
			content: [
				"This Privacy Policy describes how Nexcaliber (“Nexcaliber,” “we,” “us”) collects, uses, discloses and protects information through the Nexcaliber Member App, our insurance enrollment and benefits application for iOS and Android (the “App”)."
			]
		},
		{
			kind: "paragraph",
			content: [
				"The App is available to employees of organizations that are Nexcaliber clients and to their eligible dependents. Accounts are linked to records provided by the employer."
			]
		},
		{
			kind: "paragraph",
			content: [
				"The App may process Protected Health Information (PHI) and complies with the Health Insurance Portability and Accountability Act (HIPAA), where applicable."
			]
		}
	],
	sections: [
		{
			id: "information-we-collect",
			number: 1,
			title: "Information We Collect",
			blocks: [
				{
					kind: "list",
					items: [
						[
							{ bold: "Personal identifiers:" },
							" name, date of birth, Social Security Number, gender, marital status, member ID and employee number."
						],
						[{ bold: "Contact details:" }, " email address, phone number and mailing address."],
						[
							{ bold: "Employment and compensation:" },
							" employer, hire date, occupation, employee class, work location, pay rate, pay frequency and estimated annual salary."
						],
						[
							{ bold: "Health and insurance data (PHI):" },
							" coverage details, enrollment elections, premiums, tobacco use, answers to health questions required by some voluntary benefit plans (Statement of Insurability), claims, Explanations of Benefits (EOBs) and digital ID card details."
						],
						[
							{ bold: "Dependents and beneficiaries:" },
							" name, date of birth, Social Security Number and relationship, and for beneficiaries, address and benefit percentage."
						],
						[
							{ bold: "Enrollment acknowledgements:" },
							" your signature or acknowledgement when you complete an enrollment."
						],
						[
							{ bold: "Documents and photos:" },
							" documents and photos you choose to upload, such as proof of a life event or dependent eligibility. The App accesses your camera or photo library only when you choose to upload, and only with your permission."
						],
						[
							{ bold: "Account data:" },
							" username, password (stored in encrypted form) and email verification codes."
						],
						[
							{ bold: "Device and technical data:" },
							" your device platform (iOS or Android), a push notification token used to send you notifications, and logs of App requests kept for security and troubleshooting."
						]
					]
				},
				{
					kind: "paragraph",
					content: [
						"We do not collect your precise location, your device contacts or your browsing history. The App does not contain advertising or analytics tools."
					]
				}
			]
		},
		{
			id: "how-we-collect-information",
			number: 2,
			title: "How We Collect Information",
			blocks: [
				{
					kind: "list",
					items: [
						[
							{ bold: "From you," },
							" when you register, enroll, upload documents or update your information in the App."
						],
						[
							{ bold: "From your employer," },
							" which provides your employee record, employment and compensation details, and plan eligibility."
						],
						[
							{ bold: "From insurers and third-party administrators," },
							" which provide coverage, claims, EOB and ID card information."
						],
						[
							{ bold: "From your device," },
							" such as the push notification token, when you allow notifications."
						]
					]
				}
			]
		},
		{
			id: "how-we-use-information",
			number: 3,
			title: "How We Use Information",
			blocks: [
				{ kind: "paragraph", content: ["Information is used solely to:"] },
				{
					kind: "list",
					items: [
						["provide insurance enrollment and benefits administration;"],
						["verify your identity and secure your account, including sending email verification codes;"],
						["determine eligibility and calculate premiums;"],
						["show your coverage, claims, EOBs and ID cards;"],
						["send notifications about your enrollments, approvals, waiver requests and account changes;"],
						["provide customer support; and"],
						["comply with legal and regulatory obligations."]
					]
				}
			]
		},
		{
			id: "hipaa-compliance",
			number: 4,
			title: "HIPAA Compliance",
			blocks: [
				{
					kind: "list",
					items: [
						["Nexcaliber acts as a Business Associate where applicable."],
						[
							"PHI is processed only for permitted purposes under applicable Business Associate Agreements (BAAs)."
						],
						["Administrative, technical and physical safeguards are maintained."]
					]
				}
			]
		},
		{
			id: "sharing-of-information",
			number: 5,
			title: "Sharing of Information",
			blocks: [
				{ kind: "paragraph", content: ["Information is shared only with:"] },
				{
					kind: "list",
					items: [
						[
							"insurers, third-party administrators, employers (when applicable) and regulatory authorities;"
						],
						[
							"service providers that host and operate the App on our behalf, including cloud hosting, email delivery, and Google Firebase Cloud Messaging for push notifications; and"
						],
						[
							"our technology partner, GB Tech Service Private Limited, which develops, maintains and publishes the App on our behalf."
						]
					]
				},
				{
					kind: "paragraph",
					content: [
						"Every third party that receives information from us must protect it with the same or equal level of protection described in this Privacy Policy, and may use it only to provide services to us or as required by law."
					]
				},
				{
					kind: "paragraph",
					content: [
						{
							bold: "We do not sell your information. We do not use it for advertising, we do not track you across apps or websites owned by other companies, and we do not share it with data brokers."
						}
					]
				}
			]
		},
		{
			id: "data-security",
			number: 6,
			title: "Data Security",
			blocks: [
				{
					kind: "paragraph",
					content: [
						"We use encryption, access controls, audit logging, monitoring and industry best practices to protect confidentiality and integrity."
					]
				}
			]
		},
		{
			id: "data-retention",
			number: 7,
			title: "Data Retention",
			blocks: [
				{
					kind: "paragraph",
					content: [
						"Information is retained only as long as necessary to provide the App and to satisfy insurance, legal and regulatory requirements. When you delete your account, your information is handled as described in Section 8."
					]
				}
			]
		},
		{
			id: "account-deletion",
			number: 8,
			title: "Account and Data Deletion",
			emphasis: true,
			blocks: [
				{ kind: "paragraph", content: ["You can delete your account at any time:"] },
				{
					kind: "list",
					items: [
						[{ bold: "In the App:" }, " go to Profile > Delete Account."],
						[{ bold: "By email:" }, ` write to ${CONTACT_EMAIL} from the email address on your account.`]
					]
				},
				{ kind: "paragraph", content: ["When you delete your account:"] },
				{
					kind: "list",
					items: [
						["your login is disabled immediately and you are signed out on all devices;"],
						["your push notification tokens are removed; and"],
						["the personal data stored with your App account is deleted within 30 days."]
					]
				},
				{
					kind: "paragraph",
					content: [
						"Records that your employer, insurers or plan administrators are required by law to keep, such as enrollment elections, coverage, premium and claims records, are retained as the law requires and are not deleted with your App account. ",
						{ bold: "Deleting your App account does not cancel your insurance coverage." },
						" Contact your employer’s HR team to change your benefits."
					]
				}
			]
		},
		{
			id: "your-rights",
			number: 9,
			title: "Your Choices and Rights",
			blocks: [
				{
					kind: "list",
					items: [
						[
							{ bold: "Requests:" },
							` you may request access to, correction of or deletion of your information, an accounting of disclosures, and changes to your communication preferences, as permitted by law, by emailing ${CONTACT_EMAIL}. We may need to verify your identity before acting on a request.`
						],
						[
							{ bold: "Notifications:" },
							" you can turn off push notifications at any time in your device settings."
						],
						[
							{ bold: "Camera and photos:" },
							" you can change the App’s camera and photo access at any time in your device settings."
						],
						[
							{ bold: "Withdrawing consent:" },
							" you can stop all collection by deleting your account as described in Section 8. Records described in Section 8 are retained as the law requires."
						]
					]
				}
			]
		},
		{
			id: "children-and-dependents",
			number: 10,
			title: "Children and Dependents",
			blocks: [
				{
					kind: "paragraph",
					content: [
						"Dependent information is collected solely for coverage administration and related insurance purposes. The App is not directed to children under 13. Information about dependents who are minors is provided by the employee as their parent or guardian."
					]
				}
			]
		},
		{
			id: "policy-updates",
			number: 11,
			title: "Policy Updates",
			blocks: [
				{
					kind: "paragraph",
					content: [
						"This Privacy Policy may be updated periodically. Revisions will be posted with an updated effective date."
					]
				}
			]
		},
		{
			id: "contact",
			number: 12,
			title: "Contact Information",
			blocks: [
				{ kind: "address", lines: [["Nexcaliber"], [`Email: ${CONTACT_EMAIL}`]] },
				{
					kind: "paragraph",
					content: [
						"The Nexcaliber Member App is published on the Apple App Store by our technology partner, GB Tech Service Private Limited, on behalf of Nexcaliber."
					]
				}
			]
		}
	]
};
